import Errors from './errors';

/**
 * Map Laravel snake_case validation keys to JS field names (e.g. alcohol_percentage → alcoholPercentage).
 */
function snakeToCamel(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

export default class Forms {
  /**
   * Class constructor receives the initial fields and default values of the form and assign
   * to class atributes. Also assign the fields object that is used to render proper
   * inputs, the options object for selectboxes and the error class. Bind methods.
   *
   * @param {Object} data  The initial data fields.
   */
  constructor(data) {
    this.successMessage = '';
    this.initialData = {};
    this.fields = {};
    this.labels = {};
    this.options = {};
    this.overrideOptions = {};
    this.displays = {};
    this.hidden = [];
    this.disabled = [];
    this.tooltips = {};
    this.extraAttr = {};
    this.loading = false;
    this.quickForms = {};
    this.initialAssign(data);

    this.errors = new Errors();
    this.assignData = this.assignData.bind(this);
    this.recordErrors = this.recordErrors.bind(this);
    this.clearErrors = this.clearErrors.bind(this);
  }

  /**
   * Set quick form component for a field.
   *
   * @param {String} field The field name.
   * @param {Object} component The component to be inserted into the modal.
   */
  setQuickForm(field, component) {
    this.quickForms[field] = component;
  }

  /**
   * This method will be called to autocomplete displays.
   *
   * @param {String} field  The field to be assigned the options.
   * @param {Object} options  The options object.
   */
  setDisplays(field, displays = []) {
    this.displays[field] = displays;
  }

  /**
   * This method will be called to set selectbox or autocomplete options.
   *
   * @param {String} field  The field to be assigned the options.
   * @param {Object} options  The options object.
   */
  setOptions(field, options = []) {
    this.options[field] = options;
  }

  /**
   * This method will be called to set selectbox or autocomplete options.
   *
   * @param {String} field  The field to be assigned the options.
   * @param {Object} options  The options object.
   */
  setOverrideOptions(field, options = []) {
    this.overrideOptions[field] = options;
  }

  /**
   * This method will be called to assign specific field data.
   *
   * @param {String} field  The field to be assigned the data.
   * @param {String} value  The value to be assigned.
   */
  setFieldValue(field, value = '') {
    if (this.fields[field] === 'file') {
      if (!value) {
        this[field] = {};
      } else {
        this[field] = Object.assign({}, value);
      }
    } else {
      this[field] = value;
    }
  }

  /**
   * This method will be called to set the field type.
   *
   * @param {String} field  The field to be assigned the type.
   * @param {String} type  The type to be assigned.
   */
  setField(field, type = 'text') {
    this.fields[field] = type;
  }

  /**
   * This method will be called to set the labels.
   *
   * @param {String} field  The field to be assigned the label.
   * @param {String} label  The label to be assigned.
   */
  setLabels(field, label) {
    this.labels[field] = label;
  }

  /**
   * This method will be called to initially assign data and field types.
   *
   * @param {Object} data  The data object with fields and types.
   */
  initialAssign(data) {
    Object.keys(data).forEach((key) => {
      this.initialData[key] = data[key].value;
      if (typeof data[key] === 'string') {
        this.setField(key);
        this.setFieldValue(key, data[key]);
        return;
      }
      this.setField(key, data[key].type);
      this.setFieldValue(key, data[key].value);
      if (data[key].type === 'select' || data[key].type === 'autocomplete') {
        this.setOptions(key);
        if (data[key].type === 'autocomplete') {
          this.setDisplays(key);
        }
      }

      if (typeof data[key].attributes !== 'undefined') {
        this.setExtraAttr(key, data[key].attributes);
      }
      this.setLabels(key, data[key].label);
    });
  }

  /**
   * This method will be called when data is fetched for editing.
   *
   * @param {Object} data  The data object with fields.
   */
  assignData(data) {
    Object.keys(data).map((key) => {
      if (this[key] !== undefined) {
        this.setFieldValue(key, data[key]);
      }
      return null;
    });
  }

  /**
   * This method will be called to retrieves the form data.
   */
  data() {
    const data = Object.assign({}, this);
    delete data.errors;
    delete data.fields;
    delete data.displays;
    delete data.successMessage;
    delete data.options;
    delete data.labels;
    delete data.hidden;
    delete data.disabled;
    delete data.extraAttr;
    delete data.tooltips;
    delete data.loading;
    delete data.initialData;
    delete data.quickForms;
    return data;
  }

  /**
   * This method will be called to record the errors from validation.
   *
   * @param {Object} errors  The errors object.
   */
  recordErrors(errors) {
    if (!errors || typeof errors !== 'object') {
      this.errors.record({});
      return;
    }
    let payload = errors;
    if (payload.errors && typeof payload.errors === 'object' && !Array.isArray(payload.errors)) {
      payload = payload.errors;
    }
    const fieldKeys = Object.keys(this.fields);
    const validErrors = {};
    Object.keys(payload).forEach((key) => {
      if (key === 'message' || key === 'error') {
        return;
      }
      const val = payload[key];
      if (fieldKeys.indexOf(key) >= 0) {
        validErrors[key] = val;
        return;
      }
      const camel = snakeToCamel(key);
      if (fieldKeys.indexOf(camel) >= 0) {
        validErrors[camel] = val;
      }
    });
    this.errors.record(validErrors);
  }

  /**
   * This method will be called to record the errors from validation.
   *
   * @param {String} field  The field to clear.
   */
  clearErrors(field) {
    this.errors.clear(field);
  }

  /**
   * This method will clear the success message.
   *
   * @param {String} field  The input field name.
   * @param {String} value  The changing input value.
   */
  clearSuccess(field, value) {
    let oldValue = this[field];
    if (typeof oldValue === 'number') {
      oldValue = oldValue.toString();
    }

    if (typeof value === 'number') {
      value = value.toString();
    }

    if (oldValue !== value) {
      this.successMessage = '';
    }
  }

  /**
   * Enables field of the form.
   *
   * @param {String} field Field name.
   */
  enableField(field) {
    if (this.disabled.indexOf(field) >= 0) {
      this.disabled.splice(this.disabled.indexOf(field), 1);
    }
  }

  /**
   * Disables field of the form.
   *
   * @param {String} field Field name.
   */
  disableField(field) {
    if (this.disabled.indexOf(field) < 0) {
      this.disabled.push(field);
    }
  }

  /**
   * Return bool indicating field state.
   *
   * @param {String} field
   */
  isDisabled(field) {
    return this.disabled.indexOf(field) >= 0;
  }

  /**
   * Removes field from the form.
   *
   * @param {String} field Field name.
   */
  hideField(field) {
    if (this.hidden.indexOf(field) < 0) {
      this.hidden.push(field);
      this[field] = '';
    }
  }

  /**
   * Shows hided field.
   *
   * @param {String} field The field name.
   */
  showField(field) {
    this.hidden.splice(this.hidden.indexOf(field), 1);
  }

  /**
   * Return bool indicating field state.
   *
   * @param {String} field
   */
  isHidden(field) {
    return this.hidden.indexOf(field) >= 0;
  }

  /**
   * Set a tooltip for a given field.
   *
   * @param {String} field The field name.
   * @param {String} text The tooltip text.
   */
  setTooltip(field, text, placement = 'top-end') {
    this.tooltips[field] = {
      text,
      placement,
    };
  }

  /**
   * Set extra attributes to a input.
   *
   * @param {String} field The field name.
   * @param {Object} attrs The object with the attributes.
   */
  setExtraAttr(field, attrs = {}) {
    this.extraAttr[field] = attrs;
  }

  /**
   * Get extra attributes to a input.
   *
   * @param {String} field The field name.
   */
  getExtraAttr(field) {
    return this.extraAttr[field] ? this.extraAttr[field] : {};
  }

  /**
   * Set the extra attribute value
   *
   * @param {String} field The field name. @value
   */
  setAttrValue(field, attr, value) {
    this.extraAttr[field][attr] = value;
  }

  /**
   * Reset values to initially assigned.
   */
  resetValues() {
    this.assignData(this.initialData);
  }

  /**
   * Removes field of the form.
   *
   * @param {String} field Field name.
   */
  removeField(field) {
    delete this.fields[field];
    delete this.labels[field];
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/forms/forms.js